import { FormEvent } from "react";
import { Button, Input, Image } from "@atoms";
import { localEnv, isProductionBetaVersion } from "@config";
import { AuthSection } from "@organisms";
import { useTranslation } from "react-i18next";

const colors = require("@theme/colors") as Record<string, any>;

export interface FormValues {
  firstName: string;
  lastName: string;
  emailAddress: string;
  password: string;
  confirmPassword: string;
}

export interface FormErrorValues {
  firstName: string | undefined;
  lastName: string | undefined;
  emailAddress: string | undefined;
  password: string | undefined;
  confirmPassword: string | undefined;
}

export type SignUpPageTemplateProps = {
  values: FormValues;
  errors: FormErrorValues;
  loading: boolean;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSignInRedirect: () => void;
  handleSignUpWithGoogle: () => void;
  handleSubmit: (e?: FormEvent<HTMLFormElement> | undefined) => void;
};

export const SignUpPageTemplate = ({
  values,
  errors,
  loading,
  handleInputChange,
  handleSignInRedirect,
  handleSignUpWithGoogle,
  handleSubmit,
}: SignUpPageTemplateProps): JSX.Element => {
  const { t } = useTranslation();
  function onKeyDown(keyEvent: any) {
    if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
      keyEvent.preventDefault();
      handleSubmit()
    }
  }

  return (
    <div className="min-h-screen flex">
      <AuthSection />

      {/* Right side - Sign up form */}
      <div className="w-full lg:w-1/2 min-h-screen flex items-center justify-center bg-white">
        <div className="w-full max-w-md px-8">
          {/* Title and subtitle */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {t("auth.signup.title")}
            </h1>
            <p className="text-gray-600 text-sm">
              {t("auth.signup.subtitlePrompt")}{" "}
              <button
                onClick={handleSignInRedirect}
                className="text-primary-medium hover:text-primary-dark font-medium text-sm"
              >
                {t("auth.signup.signInLink")}
              </button>
            </p>
          </div>

          {/* Sign up form */}
          <form onSubmit={handleSubmit} onKeyDown={onKeyDown} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Input
                variant="text"
                name="firstName"
                onChange={handleInputChange}
                placeholder={t("auth.signup.firstName")}
                value={values.firstName}
                errorMessage={errors.firstName}
              />
              <Input
                variant="text"
                name="lastName"
                onChange={handleInputChange}
                placeholder={t("auth.signup.lastName")}
                value={values.lastName}
                errorMessage={errors.lastName}
              />
            </div>
            <Input
              variant="text"
              name="emailAddress"
              onChange={handleInputChange}
              placeholder={t("auth.signup.email")}
              value={values.emailAddress}
              errorMessage={errors.emailAddress}
            />
            <Input
              variant="password"
              name="password"
              onChange={handleInputChange}
              placeholder={t("auth.signup.password")}
              value={values.password}
              errorMessage={errors.password}
            />
            <Input
              variant="password"
              name="confirmPassword"
              onChange={handleInputChange}
              placeholder={t("auth.signup.confirmPassword")}
              value={values.confirmPassword}
              errorMessage={errors.confirmPassword}
            />

            {/* Sign up button */}
            <Button
              type="submit"
              variant="primary"
              label={t("auth.signup.submit")}
              stretch
              color="primary-medium"
              hoverColor="primary-dark"
              textColor="white"
              loading={loading}
            />

            {/* Separator */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">{t("auth.common.or")}</span>
              </div>
            </div>

            {/* Social sign-up buttons */}
            <div className="space-y-4">
              <Button
                variant="secondary"
                onClick={handleSignUpWithGoogle}
                label={t("auth.signup.continueWithGoogle")}
                stretch
                withIcon="google"
                color="#FFFFFF"
                hoverColor="#ECEBE7"
                textColor="#201A15"
                borderColor={colors.neutral.medium}
              />
              {/* <Button
                variant="secondary"
                onClick={() => {}} // TODO: Add Apple sign-up handler
                label="Continue with Apple"
                stretch
                color="neutral-darkest"
              />
              <Button
                variant="secondary"
                onClick={() => {}} // TODO: Add SSO sign-up handler
                label="Continue with Single Sign-On (SSO)"
                stretch
                color="neutral-darkest"
              /> */}
            </div>
          </form>

          {/* Environment indicator */}
          {localEnv === "dev" ? (
            <div className="mt-4 text-center px-4 py-2 bg-yellow-100 text-yellow-800 rounded">
              {t("auth.signup.devBanner")}
            </div>
          ) : isProductionBetaVersion && (
            <div className="mt-4 text-center px-4 py-2 bg-blue-100 text-blue-800 rounded">
              {t("auth.signup.betaBanner")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
